import type { ProductData } from "~/models/ProductData";

import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import type { SalesCampaignProductData } from "./models/SalesCampaign";
import type { AvailabilityInfo } from "~/api/pna/AvailabilityInfo";
import { useMemo } from "react";

export interface SalesData {
  campaignCode: string;
  stock: number;
  endDate?: Date;
}

export class SalesHelper {
  private packageData: Record<string, SalesData | null> = {};
  private stockFromAvailability: { [packCode: string]: number | undefined } = {};

  private constructor(
    private product: ProductData | null,
    availability: AvailabilityInfo | null,
  ) {
    if (availability) {
      availability.packagingInformation.forEach((pack) => {
        if (pack.salesStock) {
          Object.entries(pack.salesStock).forEach(([packCode, stock]) => {
            this.stockFromAvailability[packCode] = stock;
          });
        }
      });
    }
  }

  // Don't use this directly, use useSalesHelper instead
  public static of(product: ProductDescriptor | undefined | null): SalesHelper {
    if (!product) {
      return new SalesHelper(null, null);
    }

    return new SalesHelper(product.base, product.availability ?? null);
  }

  isProductOnSale() {
    if (!this.product) {
      return false;
    }
    return Object.values(this.product.salesCampaign ?? {}).some(this.internalIsPackageOnSale);
  }

  isPackageOnSale(packCode?: string) {
    if (!this.product || !packCode) {
      return false;
    }
    return this.internalIsPackageOnSale(this.product.salesCampaign?.[packCode]);
  }

  forPackage(packCode?: string): SalesData | null {
    if (!this.product || !packCode) {
      return null;
    }

    let salesData: SalesData | null = this.packageData[packCode];
    if (salesData === undefined) {
      salesData = this.constructSalesData(packCode);
      this.packageData[packCode] = salesData;
    }

    return salesData;
  }

  private constructSalesData(packCode: string): SalesData | null {
    const packData = this.product?.salesCampaign?.[packCode];
    if (!packData || !packData.length) {
      return null;
    }
    if (packData.length > 1) {
      throw new Error("Multiple sales campaign entries for pack code " + packCode);
    }
    const campaignData = packData[0];

    return {
      campaignCode: campaignData.code,
      stock: this.stockFromAvailability[packCode] ?? campaignData.quantity,
      endDate: campaignData.endDate,
    };
  }

  private internalIsPackageOnSale(packData?: SalesCampaignProductData[]) {
    if (!packData) {
      return false;
    }
    return packData.some((sale) => sale.quantity > 0);
  }
}

export function useSalesHelper(product: ProductDescriptor | undefined | null) {
  return useMemo(() => SalesHelper.of(product), [product]);
}
